import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Checkbox, pitchType, pitchSubType, degreesToClock } from 'yankees-react-utils';
import styled from 'styled-components';
import { Row, Col } from 'react-bootstrap';
import PitchTypeStats from './PitchTypeStats';
import { base64ToBlob } from '../../../../utils/heatmaps';
import StrikeZone from '../../../../components/StrikeZone';
import PitchChart from '../../../../components/PitchChart';
import { usePitchVideoFn } from '../../../../data';
import { VideoPlayerModal } from '../../../../components/VideoPlayer';

const Container = styled.div`
    margin-bottom: 20px;
    > * {
        margin-bottom: 20px;
    }
`;
const StyledRow = styled.div`
    display: flex;
    justify-content: space-around ;
    @media (max-width: 768px) {
        flex-direction: column;
    }
`;
const Header = styled.h4`
    && {
        font-size:18px;
        font-weight: bold;
        text-align: center;
    }`;
const SubHeader = styled.h5`
    font-size:16px;
    font-weight: bold;
    text-align: center;
`;
const StrikeZoneRow = styled.div`
display: flex;
justify-content: space-around;
@media (max-width: 768px) {
    flex-direction: column;
}
> * {
    // width: 45%;
    text-align: center;
    @media (max-width: 768px) {
        width: 100%;
    }
}
`;
const StyledHeatMapsContainer = styled.div`
    display: flex;
    justify-content: space-evenly;
`;
const StyledHeatMapsRow = styled(Row)`
    width: 100%;
    justify-content: space-evenly;
`;
const StyledHeatMapFilters = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    margin-bottom: 40px;
    > div > label {
        margin-top: 5px;
        margin-left: -10px;
        margin-right: 10px;
        color: #3A6A9C;
    }
`;
const StyledHeatMapFilter = styled.div`
    display: flex;
    justify-content: center;

    > div > label:after {
        top: 8px !important;
    }
    > div > label {
        height: 8px;
    }
`;
const StyledHeatMapsLabel = styled.div`
    cursor: pointer;
    line-height: 2.25;
    margin-left: -10px;
    margin-right: 15px;
    color: #3A6A9C;
    :hover {
        text-decoration: underline;
    }
`;
const StyledLegendShape = styled.div`
    height: 10px;
    width: 10px;
    margin-top: 7px;
    margin-right: 20px;
    margin-left: -10px;
    ${(props) => {
        let styles = {};
        switch (props.shape) {
        case ('hit'): {
            styles = { backgroundColor: '#FFFFFF', border: '2px solid #0000FF', transform: 'rotate(45deg)' };
            break;
        }
        case ('out'): {
            styles = { width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: '10px solid #0000FF' };
            break;
        }
        case ('wkhit'): {
            styles = { backgroundColor: '#FFFFFF', border: '2px solid #FFA500', transform: 'rotate(45deg)' };
            break;
        }
        case ('wkout'): {
            styles = { width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: '10px solid #FFA500' };
            break;
        }
        case ('ball'): {
            styles = { borderRadius: 10, backgroundColor: '#00FF00' };
            break;
        }
        case ('stk'): {
            styles = { borderRadius: 10, backgroundColor: '#FF0000' };
            break;
        }
        case ('swing'): {
            styles = { backgroundColor: '#FF0000', transform: 'rotate(45deg)' };
            break;
        }
        case ('foul'): {
            styles = { backgroundColor: '#FFFFFF', border: '2px solid #FF0000' };
            break;
        }
        default: break;
        }
        return styles;
    }}
`;

const NA = (translate) => <div style={{ padding: '150px 0', fontSize: '200%', fontStyle: 'italic', textAlign: 'center' }}>{translate('common.abbr-na')}</div>;

const filterPtLoc = (pitchLoc, filteredResults) => {
    const results = filteredResults.map((r) => r.id);
    const lhb = (pitchLoc?.lhb || []).filter((s) => results.indexOf(s.id) > -1);
    const lhbAttack = (pitchLoc?.lhb?.attack || []).filter((s) => results.indexOf(s.id) > -1);
    const lhbTwoStrikes = (pitchLoc?.lhb?.twoStrikes || []).filter((s) => results.indexOf(s.id) > -1);
    const rhb = (pitchLoc?.rhb || []).filter((s) => results.indexOf(s.id) > -1);
    const rhbAttack = (pitchLoc?.rhb?.attack || []).filter((s) => results.indexOf(s.id) > -1);
    const rhbTwoStrikes = (pitchLoc?.rhb?.twoStrikes || []).filter((s) => results.indexOf(s.id) > -1);
    return { lhb: { ...lhb, attack: lhbAttack, twoStrikes: lhbTwoStrikes }, rhb: { ...rhb, attack: rhbAttack, twoStrikes: rhbTwoStrikes } };
};

const heatMapResults = (translate) => [
    { id: 'hit', name: translate('sections.reports.pitcher.hard-hit') },
    { id: 'out', name: translate('sections.reports.pitcher.hard-out') },
    { id: 'wkhit', name: translate('sections.reports.pitcher.weak-hit') },
    { id: 'wkout', name: translate('sections.reports.pitcher.weak-out') },
    { id: 'ball', name: translate('baseball.balls_one') },
    { id: 'stk', name: translate('sections.reports.pitcher.called-k') },
    { id: 'swing', name: translate('sections.reports.pitcher.swinging-stk') },
    { id: 'foul', name: translate('sections.reports.pitcher.foul') },
];

const spinDirectionChartLabels = {
    formatter() {
        return `${degreesToClock(this.value)}`;
    },
};

const PitchTypeCharts = ({
    playerid, type, pitchtypeseries, gamestats, seasonstats, lgastats, islhp, heatMapsPts,
    heatMapsLocations, strikeZoneContours, pitchtypestdev, dataPoints, dataPointType, isInBats,
    startDate, endDate, pitchTypeSplits, isLive, livePlaylistFn, useSubType, showGameFxEra, ...rest
}) => {
    // const { state: { user } } = useContext(store);
    const { t: translate } = useTranslation();
    const [heatMapToggles, setHeatMapToggles] = useState({ pts: true, locations: false, scatter: true });
    const [heatMapResultsFilters, setHeatMapResultsFilters] = useState(heatMapResults(translate));
    // const [videoPlaylist, setVideoPlaylist] = useState(false);
    // const videoPlayerModalContainer = useRef();
    const pitchVideoFn = usePitchVideoFn();

    const pitchloc = pitchtypeseries.pitchloc[type.fxsubtype || type.pitchtype];
    const pitchlocFiltered = filterPtLoc(pitchloc, heatMapResultsFilters);
    const velo = pitchtypeseries.velo[type.fxsubtype || type.pitchtype] || [];
    const breakx = pitchtypeseries.breakx[type.fxsubtype || type.pitchtype] || [];
    const breakz = pitchtypeseries.breakz[type.fxsubtype || type.pitchtype] || [];
    const spinrate = pitchtypeseries.spinrate[type.fxsubtype || type.pitchtype] || [];
    const releasex = pitchtypeseries.releasex[type.fxsubtype || type.pitchtype] || [];
    const releasez = pitchtypeseries.releasez[type.fxsubtype || type.pitchtype] || [];
    const extension = pitchtypeseries.extension[type.fxsubtype || type.pitchtype] || [];
    const angle = pitchtypeseries.angle[type.fxsubtype || type.pitchtype] || [];
    const gyrodegree = pitchtypeseries.gyrodegree[type.fxsubtype || type.pitchtype] || [];
    const spindirection = pitchtypeseries.spindirection[type.fxsubtype || type.pitchtype] || [];
    const pitchCode = `${pitchType(type.pitchtype, true, undefined, translate)}${type.fxsubtype > 0 ? ` (${pitchSubType(type.fxsubtype, true, undefined, translate)})` : ''}`;
    const pitchName = `${pitchType(type.pitchtype, undefined, undefined, translate)}${type.fxsubtype > 0 ? ` (${pitchSubType(type.fxsubtype, undefined, undefined, translate)})` : ''}`;

    const getHeatMapSrc = (isLhb, split, heatMapType) => {
        const hm = heatMapType === 'pts' ? heatMapsPts : heatMapsLocations;
        const heatMapObj = hm.find((h) => h.PitchType === type.pitchtype && h.IsLHB === isLhb && h.Split === split);
        return heatMapObj ? base64ToBlob(heatMapObj) : null;
    };

    if (type.pitchtype !== 1 && velo.length === 0) {
        return null;
    }

    const pitchTypeStDev = pitchtypestdev.filter((st) => st.fxtype === type.pitchtype);
    return (
        <Container>
            <PitchTypeStats
                playerid={playerid}
                title={`${pitchName} ${translate('sections.post-game.ppfx.stats')}`}
                gamestats={gamestats}
                seasonstats={seasonstats}
                lgastats={lgastats}
                islhp={islhp}
                pitchtypestdev={pitchTypeStDev}
                isInBats={isInBats}
                isLive={isLive}
                livePlaylistFn={livePlaylistFn}
                startDate={startDate}
                endDate={endDate}
                pitchTypeSplits={pitchTypeSplits}
                useSubType={useSubType}
                showGameFxEra={showGameFxEra}
            />
            <>
                <div>
                    <Checkbox
                        id="pts"
                        label={translate('sections.reports.pitcher.pitcher-strengths')}
                        checked={heatMapToggles.pts}
                        onChange={() => setHeatMapToggles({ ...heatMapToggles, pts: !heatMapToggles.pts })}
                    />
                    <Checkbox
                        id="locations"
                        label={translate('sections.reports.pitcher.pitch-locations')}
                        checked={heatMapToggles.locations}
                        onChange={() => setHeatMapToggles({ ...heatMapToggles, locations: !heatMapToggles.locations })}
                    />
                    <Checkbox
                        id="scatter"
                        label={translate('sections.reports.pitcher.scatter')}
                        checked={heatMapToggles.scatter}
                        onChange={() => setHeatMapToggles({ ...heatMapToggles, scatter: !heatMapToggles.scatter })}
                    />
                </div>
                <StrikeZoneRow>
                    <StyledHeatMapsRow>
                        <Col xl={type.pitchtype === 1 ? 4 : 6}>
                            <Header>{pitchCode} {translate('sections.reports.pitcher.pitch-locations')} {translate('baseball.abbr-versus')} {translate('baseball.lhh')}</Header>
                            <StyledHeatMapsContainer>
                                <div>
                                    <SubHeader>{translate('sections.reports.pitcher.attack-counts')}</SubHeader>
                                    {pitchloc.lhb && pitchloc.lhb.attack ? (
                                        <StrikeZone
                                            enableZoom={false}
                                            contourPoints={strikeZoneContours.lhb}
                                            seriesData={pitchlocFiltered.lhb.attack}
                                            size="sm"
                                            height={250}
                                            showlegend={false}
                                            legendposition="below"
                                            yopposite
                                            showlhb
                                            showoffgrid
                                            heatMapBackground={getHeatMapSrc(true, 'Early', 'pts')}
                                            heatMapBackground2={getHeatMapSrc(true, 'attackonly', 'locations')}
                                            hideGrid
                                            showHeatMapBackground={heatMapToggles.pts}
                                            showHeatMapBackground2={heatMapToggles.locations}
                                            showSeries={heatMapToggles.scatter}
                                            showStrikeZone={false}
                                            pitchVideoFn={isInBats || isLive ? pitchVideoFn : null}
                                            videoPlayerModal={VideoPlayerModal}
                                            {...rest}
                                        />
                                    ) : NA(translate)}
                                </div>
                                <div>
                                    <SubHeader>{translate('sections.reports.pitcher.two-strikes')}</SubHeader>
                                    {pitchloc.lhb && pitchloc.lhb.twoStrikes ? (
                                        <StrikeZone
                                            enableZoom={false}
                                            contourPoints={strikeZoneContours.lhb}
                                            seriesData={pitchlocFiltered.lhb.twoStrikes}
                                            size="sm"
                                            height={250}
                                            showlegend={false}
                                            legendposition="below"
                                            yopposite
                                            showlhb
                                            showoffgrid
                                            heatMapBackground={getHeatMapSrc(true, 'TwoStrikes', 'pts')}
                                            heatMapBackground2={getHeatMapSrc(true, '2konly', 'locations')}
                                            hideGrid
                                            showHeatMapBackground={heatMapToggles.pts}
                                            showHeatMapBackground2={heatMapToggles.locations}
                                            showSeries={heatMapToggles.scatter}
                                            showStrikeZone={false}
                                            pitchVideoFn={isInBats || isLive ? pitchVideoFn : null}
                                            videoPlayerModal={VideoPlayerModal}
                                            {...rest}
                                        />
                                    ) : NA(translate)}
                                </div>
                            </StyledHeatMapsContainer>
                        </Col>
                        <Col xl={type.pitchtype === 1 ? 4 : 6}>
                            <Header>{pitchCode} {translate('sections.reports.pitcher.pitch-locations')} {translate('baseball.abbr-versus')} {translate('baseball.rhh')}</Header>
                            <StyledHeatMapsContainer>
                                <div>
                                    <SubHeader>{translate('sections.reports.pitcher.attack-counts')}</SubHeader>
                                    {pitchloc.rhb && pitchloc.rhb.attack ? (
                                        <StrikeZone
                                            enableZoom={false}
                                            contourPoints={strikeZoneContours.rhb}
                                            seriesData={pitchlocFiltered.rhb.attack}
                                            size="sm"
                                            height={250}
                                            showlegend={false}
                                            legendposition="below"
                                            yopposite
                                            showoffgrid
                                            heatMapBackground={getHeatMapSrc(false, 'Early', 'pts')}
                                            heatMapBackground2={getHeatMapSrc(false, 'attackonly', 'locations')}
                                            hideGrid
                                            showHeatMapBackground={heatMapToggles.pts}
                                            showHeatMapBackground2={heatMapToggles.locations}
                                            showSeries={heatMapToggles.scatter}
                                            showStrikeZone={false}
                                            pitchVideoFn={isInBats || isLive ? pitchVideoFn : null}
                                            videoPlayerModal={VideoPlayerModal}
                                            {...rest}
                                        />
                                    ) : NA(translate)}
                                </div>
                                <div>
                                    <SubHeader>{translate('sections.reports.pitcher.two-strikes')}</SubHeader>
                                    {pitchloc.rhb && pitchloc.rhb.twoStrikes ? (
                                        <StrikeZone
                                            enableZoom={false}
                                            contourPoints={strikeZoneContours.rhb}
                                            seriesData={pitchlocFiltered.rhb.twoStrikes}
                                            size="sm"
                                            height={250}
                                            showlegend={false}
                                            legendposition="below"
                                            yopposite
                                            showoffgrid
                                            heatMapBackground={getHeatMapSrc(false, 'TwoStrikes', 'pts')}
                                            heatMapBackground2={getHeatMapSrc(false, '2konly', 'locations')}
                                            hideGrid
                                            showHeatMapBackground={heatMapToggles.pts}
                                            showHeatMapBackground2={heatMapToggles.locations}
                                            showSeries={heatMapToggles.scatter}
                                            showStrikeZone={false}
                                            pitchVideoFn={isInBats || isLive ? pitchVideoFn : null}
                                            videoPlayerModal={VideoPlayerModal}
                                            {...rest}
                                        />
                                    ) : NA(translate)}
                                </div>
                            </StyledHeatMapsContainer>
                        </Col>
                        {type.pitchtype === 1 && (
                            <Col xl={type.pitchtype === 1 ? 4 : 6}>
                                <Header>{translate('sections.reports.pitcher.extension-velo-injury')}</Header>
                                <PitchChart
                                    enableZoom={false}
                                    seriesData={pitchtypeseries.pitchinjuryrisk[type.fxsubtype || type.pitchtype]}
                                    titleX={dataPointType === 'Game' ? translate('sections.reports.pitcher.game') : translate('sections.reports.pitcher.pitch-number')}
                                    titleY={`${translate('sections.reports.pitcher.injury-risk')} (%)`}
                                    pitches={dataPoints}
                                    width={400}
                                    scaleY={0.5}
                                    subscaleY={0.1}
                                    minY={15.5}
                                    maxY={18.5}
                                    yplotbands={[{ color: 'rgba(255,0,0,0.3)', from: 17.8, to: 99.9 }]}
                                    {...rest}
                                />
                            </Col>
                        )}
                    </StyledHeatMapsRow>
                </StrikeZoneRow>
                <StyledHeatMapFilters>
                    {heatMapResults(translate).map((r) => (
                        <StyledHeatMapFilter key={r.id}>
                            <Checkbox
                                key={`${r.id}_2`}
                                id={r.id}
                                checked={heatMapResultsFilters.filter((f) => f.id === r.id).length > 0}
                                onChange={(e) => (e.currentTarget.checked ? setHeatMapResultsFilters([...heatMapResultsFilters, r]) : setHeatMapResultsFilters(heatMapResultsFilters.filter((f) => f.id !== r.id)))}
                            />
                            <StyledHeatMapsLabel onClick={() => setHeatMapResultsFilters([r])}>{r.name}</StyledHeatMapsLabel>
                            <StyledLegendShape shape={r.id} />
                        </StyledHeatMapFilter>
                    ))}
                </StyledHeatMapFilters>
            </>
            {type.pitchtype !== 1 && (
                <>
                    <div>
                        <StyledRow>
                            <div>
                                <Header>{pitchCode} {translate('components.search.filter.horizontal-break')}</Header>
                                <PitchChart
                                    enableZoom={false}
                                    seriesData={breakx}
                                    minX={startDate}
                                    xAxisType={dataPointType === 'Game' ? 'datetime' : null}
                                    titleX={dataPointType === 'Game' ? translate('sections.reports.pitcher.game') : translate('sections.reports.pitcher.pitch-number')}
                                    titleY={translate('pitcher-overview.pitch-break.horiz-break')}
                                    pitches={dataPoints}
                                    {...rest}
                                />
                            </div>
                            <div>
                                <Header>{pitchCode} {translate('components.search.filter.vertical-break')}</Header>
                                <PitchChart
                                    enableZoom={false}
                                    seriesData={breakz}
                                    minX={startDate}
                                    xAxisType={dataPointType === 'Game' ? 'datetime' : null}
                                    titleX={dataPointType === 'Game' ? translate('sections.reports.pitcher.game') : translate('sections.reports.pitcher.pitch-number')}
                                    titleY={translate('pitcher-overview.pitch-break.vert-break')}
                                    pitches={dataPoints}
                                    {...rest}
                                />
                            </div>
                        </StyledRow>
                    </div>
                    <div>
                        <StyledRow>
                            <div>
                                <Header>{pitchCode} {translate('sections.reports.pitcher.spin-rate')}</Header>
                                <PitchChart
                                    enableZoom={false}
                                    seriesData={spinrate}
                                    minX={startDate}
                                    xAxisType={dataPointType === 'Game' ? 'datetime' : null}
                                    titleX={dataPointType === 'Game' ? translate('sections.reports.pitcher.game') : translate('sections.reports.pitcher.pitch-number')}
                                    titleY={`${translate('sections.reports.pitcher.spin-rate')} (x1000 rpm)`}
                                    pitches={dataPoints}
                                    {...rest}
                                />
                            </div>
                            <div>
                                <Header>{pitchCode} {translate('baseball.pitches_one')} {translate('sections.reports.pitcher.velocity')}</Header>
                                <PitchChart
                                    enableZoom={false}
                                    seriesData={velo}
                                    minX={startDate}
                                    xAxisType={dataPointType === 'Game' ? 'datetime' : null}
                                    titleX={dataPointType === 'Game' ? translate('sections.reports.pitcher.game') : translate('sections.reports.pitcher.pitch-number')}
                                    titleY={translate('sections.reports.pitcher.velocity')}
                                    pitches={dataPoints}
                                    {...rest}
                                />
                            </div>
                        </StyledRow>
                    </div>
                    <div>
                        <StyledRow>
                            <div>
                                <Header>{pitchCode} {translate('sections.reports.pitcher.release-point-side')}</Header>
                                <PitchChart
                                    enableZoom={false}
                                    seriesData={releasex}
                                    minX={startDate}
                                    xAxisType={dataPointType === 'Game' ? 'datetime' : null}
                                    titleX={dataPointType === 'Game' ? translate('sections.reports.pitcher.game') : translate('sections.reports.pitcher.pitch-number')}
                                    titleY={translate('sections.reports.pitcher.release-point-side')}
                                    pitches={dataPoints}
                                    {...rest}
                                />
                            </div>
                            <div>
                                <Header>{pitchCode} {translate('sections.reports.pitcher.release-point-height')}</Header>
                                <PitchChart
                                    enableZoom={false}
                                    seriesData={releasez}
                                    minX={startDate}
                                    xAxisType={dataPointType === 'Game' ? 'datetime' : null}
                                    titleX={dataPointType === 'Game' ? translate('sections.reports.pitcher.game') : translate('sections.reports.pitcher.pitch-number')}
                                    titleY={translate('sections.reports.pitcher.release-point-height')}
                                    pitches={dataPoints}
                                    {...rest}
                                />
                            </div>
                        </StyledRow>
                    </div>
                    <div>
                        <StyledRow>
                            <div>
                                <Header>{pitchCode} {translate('sections.reports.pitcher.extension')}</Header>
                                <PitchChart
                                    enableZoom={false}
                                    seriesData={extension}
                                    minX={startDate}
                                    xAxisType={dataPointType === 'Game' ? 'datetime' : null}
                                    titleX={dataPointType === 'Game' ? translate('sections.reports.pitcher.game') : translate('sections.reports.pitcher.pitch-number')}
                                    titleY={translate('sections.reports.pitcher.extension')}
                                    pitches={dataPoints}
                                    {...rest}
                                />
                            </div>
                            <div>
                                <Header>{pitchCode} {translate('data.arm-angle')}</Header>
                                <PitchChart
                                    enableZoom={false}
                                    seriesData={angle}
                                    minX={startDate}
                                    xAxisType={dataPointType === 'Game' ? 'datetime' : null}
                                    titleX={dataPointType === 'Game' ? translate('sections.reports.pitcher.game') : translate('sections.reports.pitcher.pitch-number')}
                                    titleY={translate('data.arm-angle')}
                                    pitches={dataPoints}
                                    {...rest}
                                />
                            </div>
                        </StyledRow>
                    </div>
                    <div>
                        <StyledRow>
                            <div>
                                <Header>{pitchCode} {translate('sections.reports.pitcher.gyro-degree')}</Header>
                                <PitchChart
                                    enableZoom={false}
                                    seriesData={gyrodegree}
                                    minX={startDate}
                                    xAxisType={dataPointType === 'Game' ? 'datetime' : null}
                                    titleX={dataPointType === 'Game' ? translate('sections.reports.pitcher.game') : translate('sections.reports.pitcher.pitch-number')}
                                    titleY={translate('sections.reports.pitcher.gyro-degree')}
                                    pitches={dataPoints}
                                    {...rest}
                                />
                            </div>
                            <div>
                                <Header>{pitchCode} {translate('data.spin-direction')}</Header>
                                <PitchChart
                                    enableZoom={false}
                                    seriesData={spindirection}
                                    minX={startDate}
                                    xAxisType={dataPointType === 'Game' ? 'datetime' : null}
                                    titleX={dataPointType === 'Game' ? translate('sections.reports.pitcher.game') : translate('sections.reports.pitcher.pitch-number')}
                                    titleY={translate('data.spin-direction')}
                                    pitches={dataPoints}
                                    yAxisLabels={spinDirectionChartLabels}
                                    yTooltipFormatter={degreesToClock}
                                    {...rest}
                                />
                            </div>
                        </StyledRow>
                    </div>
                </>
            )}
        </Container>
    );
};

PitchTypeCharts.propTypes = {
    playerid: PropTypes.number.isRequired,
    type: PropTypes.shape({
        pitchtype: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        fxsubtype: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }).isRequired,
    pitchtypeseries: PropTypes.shape({
        pitchloc: PropTypes.shape({}).isRequired,
        velo: PropTypes.shape({}).isRequired,
        breakx: PropTypes.shape({}).isRequired,
        breakz: PropTypes.shape({}).isRequired,
        spinrate: PropTypes.shape({}).isRequired,
        releasex: PropTypes.shape({}).isRequired,
        releasez: PropTypes.shape({}).isRequired,
        extension: PropTypes.shape({}).isRequired,
        pitchinjuryrisk: PropTypes.shape({}).isRequired,
        angle: PropTypes.shape({}).isRequired,
        gyrodegree: PropTypes.shape({}).isRequired,
        spindirection: PropTypes.shape({}).isRequired,
    }).isRequired,
    gamestats: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    seasonstats: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    lgastats: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    islhp: PropTypes.bool,
    heatMapsPts: PropTypes.arrayOf(PropTypes.shape({})),
    heatMapsLocations: PropTypes.arrayOf(PropTypes.shape({})),
    strikeZoneContours: PropTypes.shape({
        lhb: PropTypes.arrayOf(PropTypes.shape({})),
        rhb: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    pitchtypestdev: PropTypes.arrayOf(PropTypes.shape({})),
    dataPoints: PropTypes.number.isRequired,
    dataPointType: PropTypes.oneOf(['PitchNumber', 'Game']),
    isInBats: PropTypes.bool,
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    pitchTypeSplits: PropTypes.arrayOf(PropTypes.shape({})),
    isLive: PropTypes.bool,
    useSubType: PropTypes.bool,
    livePlaylistFn: PropTypes.func,
    showGameFxEra: PropTypes.bool,
};
PitchTypeCharts.defaultProps = {
    islhp: undefined,
    heatMapsPts: [],
    heatMapsLocations: [],
    strikeZoneContours: {
        lhb: [],
        rhb: [],
    },
    pitchtypestdev: [],
    dataPointType: 'PitchNumber',
    isInBats: true,
    startDate: null,
    endDate: null,
    pitchTypeSplits: null,
    isLive: false,
    useSubType: false,
    livePlaylistFn: () => {},
    showGameFxEra: false,
};

export default PitchTypeCharts;
